import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const MASTER_BASE = () => `${config.BASE_URL}/property-assets-master`;
const ASSETS_BASE = () => `${config.BASE_URL}/property-assets`;

async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

function messageForResponse(res, payload, fallback) {
  if (res.status >= 500) {
    return "Internal server error. Please try again later.";
  }
  const m = payload?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

async function authJson(method, url, { body, csrfToken } = {}) {
  const headers = buildAuthHeaders(
    csrfToken ? { "X-CSRF-Token": csrfToken } : {},
  );
  const isFormData = body instanceof FormData;
  if (isFormData) delete headers["Content-Type"];

  const res = await authFetch(url, {
    method,
    credentials: "include",
    headers,
    body: body != null ? body : undefined,
  });
  const payload = await parseJson(res);
  return { res, payload };
}

async function authGet(url) {
  const res = await authFetch(url, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(messageForResponse(res, data, "Request failed"));
  }
  return data;
}

async function authMutate(method, url, body, csrfToken, fallbackMsg) {
  try {
    const { res, payload } = await authJson(method, url, { body, csrfToken });
    if (!res.ok) {
      return {
        data: null,
        error: {
          message: messageForResponse(res, payload, fallbackMsg),
          details: res.status >= 500 ? undefined : payload.details,
        },
      };
    }
    return { data: payload, error: null, reactivated: payload.reactivated };
  } catch (error) {
    return {
      data: null,
      error: {
        message:
          typeof error?.message === "string"
            ? error.message
            : fallbackMsg,
      },
    };
  }
}

/* =========================================================
   MASTER — CATEGORIES
========================================================= */

export const fetchPropertyCategories = () =>
  authGet(`${MASTER_BASE()}/property-categories`);

export const fetchPropertyCategoryDropdown = () =>
  authGet(`${MASTER_BASE()}/property-category-dropdown`);

export const createPropertyCategory = (data, csrfToken) =>
  authMutate(
    "POST",
    `${MASTER_BASE()}/property-category`,
    JSON.stringify(data),
    csrfToken,
    "Failed to create category",
  );

export const updatePropertyCategory = (id, data, csrfToken) =>
  authMutate(
    "PUT",
    `${MASTER_BASE()}/property-category/${encodeURIComponent(String(id))}`,
    JSON.stringify(data),
    csrfToken,
    "Failed to update category",
  );

export const deletePropertyCategory = async (id, csrfToken) => {
  const { res, payload } = await authJson(
    "DELETE",
    `${MASTER_BASE()}/property-category/${encodeURIComponent(String(id))}`,
    { csrfToken },
  );
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to delete category"),
    );
  }
  return payload;
};

/* =========================================================
   MASTER — CONDITIONS
========================================================= */

export const fetchPropertyConditions = () =>
  authGet(`${MASTER_BASE()}/property-conditions`);

export const fetchPropertyConditionDropdown = () =>
  authGet(`${MASTER_BASE()}/property-condition-dropdown`);

export const createPropertyCondition = (data, csrfToken) =>
  authMutate(
    "POST",
    `${MASTER_BASE()}/property-condition`,
    JSON.stringify(data),
    csrfToken,
    "Failed to create condition",
  );

export const updatePropertyCondition = (id, data, csrfToken) =>
  authMutate(
    "PUT",
    `${MASTER_BASE()}/property-condition/${encodeURIComponent(String(id))}`,
    JSON.stringify(data),
    csrfToken,
    "Failed to update condition",
  );

export const deletePropertyCondition = async (id, csrfToken) => {
  const { res, payload } = await authJson(
    "DELETE",
    `${MASTER_BASE()}/property-condition/${encodeURIComponent(String(id))}`,
    { csrfToken },
  );
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to delete condition"),
    );
  }
  return payload;
};

/* =========================================================
   MASTER — ASSET STATUSES
========================================================= */

export const fetchPropertyStatuses = () =>
  authGet(`${MASTER_BASE()}/property-statuses`);

export const fetchPropertyStatusDropdown = () =>
  authGet(`${MASTER_BASE()}/property-status-dropdown`);

export const createPropertyStatus = (data, csrfToken) =>
  authMutate(
    "POST",
    `${MASTER_BASE()}/property-status`,
    JSON.stringify(data),
    csrfToken,
    "Failed to create asset status",
  );

export const updatePropertyStatus = (id, data, csrfToken) =>
  authMutate(
    "PUT",
    `${MASTER_BASE()}/property-status/${encodeURIComponent(String(id))}`,
    JSON.stringify(data),
    csrfToken,
    "Failed to update asset status",
  );

export const deletePropertyStatus = async (id, csrfToken) => {
  const { res, payload } = await authJson(
    "DELETE",
    `${MASTER_BASE()}/property-status/${encodeURIComponent(String(id))}`,
    { csrfToken },
  );
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to delete asset status"),
    );
  }
  return payload;
};

/* =========================================================
   ASSETS (tbl)
========================================================= */

export const fetchPropertyAssets = () =>
  authGet(`${ASSETS_BASE()}/all`);

export const fetchPropertyAssetById = (id) =>
  authGet(`${ASSETS_BASE()}/${encodeURIComponent(String(id))}`);

export const searchResponsiblePersons = (q) => {
  const term = String(q ?? "").trim();
  const qs = term ? `?q=${encodeURIComponent(term)}` : "";
  return authGet(`${ASSETS_BASE()}/responsible-persons/search${qs}`);
};

export const createPropertyAsset = (formData, csrfToken) =>
  authMutate(
    "POST",
    `${ASSETS_BASE()}/create`,
    formData,
    csrfToken,
    "Failed to create property asset",
  );

export const updatePropertyAsset = (id, formData, csrfToken) =>
  authMutate(
    "PUT",
    `${ASSETS_BASE()}/update/${encodeURIComponent(String(id))}`,
    formData,
    csrfToken,
    "Failed to update property asset",
  );

export const deletePropertyAsset = async (id, csrfToken) => {
  const { res, payload } = await authJson(
    "DELETE",
    `${ASSETS_BASE()}/delete/${encodeURIComponent(String(id))}`,
    { csrfToken },
  );
  if (!res.ok) {
    throw new Error(
      messageForResponse(res, payload, "Failed to delete property asset"),
    );
  }
  return payload;
};
